import { Renderer } from "@with-nx/cms-ui";
import { useSimpleShoppingCart } from "@with-nx/ecommerce";
import { Tooltip } from "@with-nx/editor-ui";
import {
  AnalyticsHelper,
  Currency,
  fetchCMSClientsSSR,
  fetchCMSPartnersSSR,
  Formatter,
  getLicensorImage,
  GoogleSchema,
  requestShowBrandOnlyShowSSR,
  requestVariantSSR,
  useMobile,
  usePricing,
  useSearchPricing,
} from "@with-nx/hooks-n-helpers";
import { useServerRegion } from "@with-nx/region";
import { DesignedButton, ReusableGlossBox } from "@with-nx/simple-ui/atoms";
import { DesignedCard, ReusableSection, Rule } from "@with-nx/simple-ui/atoms";
import {
  AuthenticationLockContainer,
  CatalogLayout,
  CatalogLayoutWait,
  ClientDetails,
  ReusableSimpleHeroSection,
} from "@with-nx/simple-ui/organisms";
import {
  ClientStaticData,
  PartnerStaticData,
  ShowDataStatic,
} from "@with-nx/static";
import { Col, Row } from "antd";
import { useRouter } from "next/router";
import { Box } from "simple-effing-primitive-layout";

import { ShowsTab } from "../../../../../components/w/shows/ShowsTab";
import { useEffect } from "react";
import { WaitListFormContainer } from "../../../../../components/w/scene-previews/WaitListFormContainer";
import { useAuth } from "@with-nx/auth";

export const getServerSideProps = async (context) => {
  const { resolvedUrl } = context;
  const { variant } = context.params;

  const showVariant = !ShowDataStatic.isShowsEmpty()
    ? ShowDataStatic.getShowBySlug(variant) ||
      (await requestVariantSSR(variant))
    : await requestVariantSSR(variant);

  const clients = !ClientStaticData.isClientsEmpty()
    ? ClientStaticData.getClientByShowVariantTag(showVariant?.id?.toString()) ||
      (await fetchCMSClientsSSR({
        filter: {
          showVariantTags: [showVariant?.id?.toString()],
        },
      }))
    : await fetchCMSClientsSSR({
        filter: {
          showVariantTags: [showVariant?.id?.toString()],
        },
      });

  const client =
    clients.find((item) => item?.clientType === "Choreographer") || null;

  const partners = !PartnerStaticData.isPartnersEmpty()
    ? PartnerStaticData.getAllPartners() || (await fetchCMSPartnersSSR({}))
    : await fetchCMSPartnersSSR({});

  const [showOnly] = await (async () => {
    try {
      const [showOnly] = await Promise.all([
        requestShowBrandOnlyShowSSR(showVariant?.showBrand?.slug),
      ]);

      return [showOnly];
    } catch {
      return [{ only: true }];
    }
  })();

  return {
    props: {
      resolvedUrl,
      showVariant,
      client,
      partners,
      showOnly,
    },
  };
};

export default function Page({
  resolvedUrl,
  showVariant,
  client,
  partners,
  showOnly,
}) {
  const router = useRouter();
  const choreoGuide = showVariant?.choreoGuide;

  const region = useServerRegion();
  const pricing = usePricing({ type: "CHOREO_GUIDE", region: region?.id });

  const licenses = useSearchPricing({
    search: showVariant?.title,
    type: "CHOREO_GUIDE",
    choreoGuideId: choreoGuide?.id,
    region: region?.id,
  });
  const mobile = useMobile();

  const auth = useAuth();

  const basket = useSimpleShoppingCart();
  const licensor = licenses?.pricing?.[0]?.licensors?.[0]?.licensor;
  const _licensor = getLicensorImage(
    partners,
    licensor?.name,
    licensor?.abbreviation
  );

  useEffect(() => {
    AnalyticsHelper.collect(showVariant?.id);
  }, [showVariant?.id]);

  if (!showVariant?.id) {
    return null;
  }

  let shouldHideLogo =
    (!showVariant?.scenicProjections.length && !showVariant?.choreoGuide) ||
    !licenses?.pricing?.length;

  if (showVariant?.forceShowLogo) {
    shouldHideLogo = false;
  }

  const waitlist = !choreoGuide || !licenses?.pricing?.length;

  const showLogo = shouldHideLogo
    ? undefined
    : showVariant?.attachments?.find((i) => i.name === "logo")?.blob.signedUrl;

  return (
    <CatalogLayout
      footerNoAngle
      transparentFixedNavBar
      title={
        choreoGuide?.metaTitle
          ? choreoGuide?.metaTitle
          : showVariant?.title
          ? `${showVariant?.title} Digital Backdrops for Theatre | Broadway Media`
          : undefined
      }
      description={
        choreoGuide?.metaDescription
          ? choreoGuide?.metaDescription
          : showVariant?.marketingDescription
      }
      resolvedUrl={resolvedUrl}
    >
      <CatalogLayoutWait until={[!licenses.loading]}>
        <ReusableSimpleHeroSection
          image={
            showVariant?.attachments?.find((i) => i.name === "image")?.blob
              .signedUrl
          }
          press={() => {
            router.push(`/shows/${router.query?.brand}`);
          }}
          show={{
            name: showVariant?.title,
            image: showLogo,
            color: showVariant?.logoColor || showVariant?.showBrand?.logoColor,
          }}
          title={showVariant?.title}
          description={showVariant?.marketingDescription}
          licensor={
            _licensor
              ? {
                  name: licensor?.name || licensor?.abbreviation,
                  image: Formatter.image(_licensor.image),
                  url: `/partners/${_licensor?.slug}`,
                }
              : undefined
          }
        />

        <div className="container">
          <ShowsTab
            waitlist={showVariant?.scenicProjections?.length === 0}
            empty={false}
            loading={false}
            showVariant={showVariant}
            onlyShow={showOnly.only}
            currentTab="Choreography Guides"
          />
        </div>

        <ReusableSection>
          <div className="container">
            <Row gutter={[36, 36]} justify="space-between">
              <Col xs={24} sm={24} md={12} lg={10}>
                <DesignedCard
                  size="16x9"
                  embed={
                    choreoGuide?.commercialVideoUrl ||
                    "https://www.youtube.com/watch?v=3lFGV56ERMk"
                  }
                  pop={false}
                  properties={{
                    bottom: 24,
                  }}
                  muted={false}
                  playing={false}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={14}>
                {waitlist ? (
                  <Rule parse="!hl d:block mb:12 c:?primary" tag="span">
                    Choreography Guides Coming Soon!
                  </Rule>
                ) : undefined}
                <Renderer
                  data={{
                    blocks:
                      waitlist && choreoGuide?.marketingDescription
                        ? [
                            {
                              type: "header",
                              data: {
                                level: 2,
                                text: "About Choreography Guides",
                              },
                            },
                            {
                              type: "paragraph",
                              data: {
                                text: choreoGuide?.marketingDescription,
                              },
                            },
                          ]
                        : [
                            {
                              type: "header",
                              data: {
                                level: 2,
                                text: "About Choreography Guides",
                              },
                            },
                            {
                              type: "paragraph",
                              data: {
                                text: "Now, you can create stunning productions with some help from Broadway’s best with Choreography Guides. Inspire your cast and enrich the theatrical experience as you learn the choreography, the stories behind the steps, and the vision from the original creators.",
                              },
                            },
                            {
                              type: "paragraph",
                              data: {
                                text: "Choreography Video Guides are easy-to-use digital video guides, which include 3 – 4 hours of instruction, and time-saving tips on how to adapt the choreography for the unique needs of your production.",
                              },
                            },
                          ],
                  }}
                />
                {waitlist ? undefined : licenses.pricing?.length === 0 ? (
                  <Box parse="mt:24 d:flex fd:column">
                    <Box parse="d:flex a:baseline mb:16">
                      <Rule rule="ll" size={48} line={56}>
                        {Currency.format(parseFloat(pricing?.price), "USD")}
                      </Rule>
                      <Rule rule="lm" parse="ml:5">
                        1-year license
                      </Rule>
                    </Box>
                    <DesignedButton
                      label="Order With a Representative"
                      theme="tetriary-accent"
                      size="large"
                      href={`/contact/start?mode=order-with-representative&referrer=${router.asPath}`}
                      properties={{ left: 10 }}
                    />
                  </Box>
                ) : (
                  licenses?.pricing.map((license, l) => {
                    const isInCart = basket.added(license.id);

                    return (
                      <Box parse="mt:24 d:flex fd:column" key={l}>
                        <GoogleSchema
                          data={{
                            id: String(pricing.id),
                            name: `${showVariant?.title} Choreography Guides - 1 Year License`,
                            price: license.price,
                            currency: region?.currency.code,
                            unit: "1 year",
                            image: showLogo,
                          }}
                        />
                        <Box
                          parse="d:flex a:baseline mb:16"
                          style={{ gap: 10 }}
                        >
                          <Rule rule="ll" size={48} line={56}>
                            {license.pricing.price}
                          </Rule>
                          <Rule rule="lm" parse="ml:5">
                            1-year license
                          </Rule>
                        </Box>
                        <Box parse="d:flex fw:wrap" style={{ gap: 10 }}>
                          <DesignedButton
                            label={
                              isInCart ? "Added to Cart" : "Purchase License"
                            }
                            theme={isInCart ? "positive" : "primary"}
                            size="large"
                            press={() => {
                              if (basket.authenticated) {
                                basket
                                  .add(license.id, "CHOREO_GUIDE")
                                  .then(() => router.push("/checkout"));
                              } else {
                                const authenticate = () => {
                                  if (!window) {
                                    return;
                                  }

                                  const _url = new URL(
                                    window?.location?.origin + "/auth"
                                  );
                                  _url.search = new URLSearchParams({
                                    redirect: window.location.pathname,
                                    action: "Continue",
                                    product: license.id as string,
                                    type: "CHOREO_GUIDE",
                                    form: "register",
                                  }).toString();
                                  router.push(_url.toString());
                                };

                                authenticate();
                              }
                            }}
                            properties={{ right: 10 }}
                          />
                          <Box parse="d:flex a:center fd:column">
                            <Box parse="w:1 f:1 c:?entity" />
                            <Rule parse="!lt c:?entity mt:4 mb:4">OR</Rule>
                            <Box parse="w:1 f:1 c:?entity" />
                          </Box>
                          <Tooltip
                            position="top-center"
                            label="Looking for additional support in purchasing this license? Feel free to contact our representatives."
                          >
                            <DesignedButton
                              label="Order With a Representative"
                              theme="tetriary-accent"
                              size="large"
                              href={`/contact/start?mode=order-with-representative&referrer=${router.asPath}`}
                            />
                          </Tooltip>

                          {region.id === 1 ? (
                            <DesignedButton
                              label="Download Order Form"
                              theme="tetriary-accent"
                              size="large"
                              press={() => {
                                router.push("/legal/documents/order-form");
                              }}
                            />
                          ) : undefined}
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Col>
              {!waitlist ? (
                <Col xs={24}>
                  <Rule
                    rule="hl"
                    color="var(--font1)"
                    bottom={24}
                    display="block"
                  >
                    About the {showVariant?.title} Choreography Guides
                  </Rule>
                  <Rule
                    rule="pl"
                    color="var(--font2)"
                    bottom={24}
                    display="block"
                  >
                    {choreoGuide?.marketingDescription ||
                      showVariant?.marketingDescription}
                  </Rule>
                </Col>
              ) : undefined}
            </Row>
          </div>
        </ReusableSection>
        {client && !waitlist ? (
          <ReusableSection>
            <div className="container">
              <Rule rule="hl" color="var(--font1)" bottom={24} display="block">
                Choreographer
              </Rule>
              <ClientDetails
                image={Formatter.image(client?.image)}
                url={`/clients/${client?.slug}`}
                name={client.title}
                description={client.description}
              />
            </div>
          </ReusableSection>
        ) : undefined}
        {waitlist ? (
          <ReusableSection>
            <div className="container">
              <WaitListFormContainer
                title="Join the Waitlist for Choreography Guides"
                description="Choreography Guides for this title are currently unavailable. Sign-up to be among the first to know when this title is released."
                productType="choreography-guides"
                restricted={auth.authState.status !== "authenticated"}
                showId={showVariant?.id}
              />
            </div>
          </ReusableSection>
        ) : (
          <Box parse="w:100% pt:48 pb:48 p:relative i:1 z:1">
            <div className="container">
              <Rule rule="hl" color="var(--font1)" bottom={24} display="block">
                Browse the Choreography Videos
              </Rule>
            </div>
            <AuthenticationLockContainer
              bypass={mobile}
              render={(authenticated: boolean) => {
                if (mobile && !authenticated) {
                  return (
                    <div className="container">
                      <ReusableGlossBox parse="pa:20 mb:24 br:10 c:?foreground p:relative oa:hidden">
                        <Rule parse="!ht d:block mb:12">Sign-Up / Sign-In</Rule>
                        <Rule parse="!pl d:block mb:12 c:?font3">
                          Sign in or sign up to see scene previews
                        </Rule>
                        <Box parse="d:flex a:center">
                          <DesignedButton
                            label="Sign-Up"
                            href="/auth?form=register&open=back"
                            theme="primary"
                            size="medium"
                            properties={{ right: 10 }}
                          />
                          <DesignedButton
                            label="Sign-In"
                            href="/auth?form=login&open=back"
                            theme="tetriary"
                            size="medium"
                            properties={{ right: 10 }}
                          />
                        </Box>
                      </ReusableGlossBox>
                    </div>
                  );
                }

                return (
                  <div className="container">
                    <Row gutter={[36, 18]}>
                      {choreoGuide?.songs
                        ?.sort((a, b) => {
                          if (a.position < b.position) {
                            return -1;
                          }

                          if (a.position > b.position) {
                            return 1;
                          }

                          return 0;
                        })
                        ?.map((item, i) => (
                          <Col xs={24} sm={24} md={12} lg={6} key={item.id}>
                            <DesignedCard
                              size="16x9"
                              playable={true}
                              watermark={true}
                              video={
                                !authenticated
                                  ? undefined
                                  : item.attachments.find(
                                      (i) => i.name === "preview_video"
                                    )?.blob?.signedUrl
                              }
                              image={
                                item.attachments.find(
                                  (i) => i.name === "preview_still"
                                )?.blob?.signedUrl
                              }
                              pop={true}
                              outside={{
                                title: item.name,
                                subtitle: i === 0 ? null : `Song ${i}`,
                              }}
                            />
                          </Col>
                        ))}
                    </Row>
                  </div>
                );
              }}
            />
          </Box>
        )}
      </CatalogLayoutWait>
    </CatalogLayout>
  );
}
